import type {
  ExperienceContent,
  ExperienceDraftContent,
  ExperienceVariableKind,
  VariableScopeReason,
} from '../schemas/experience.js';
import { NOYAU_TOKEN, NOYAU_VARIABLE_ID } from '../schemas/noyau.js';
import type { VersionedRef } from '../schemas/versioning.js';
import { blockVariableTexts } from './block-texts.js';
import { firstChoiceOf } from './noyau.js';
import { isFieldBlock, pageBlocks, pageChoiceName } from './play.js';

/**
 * The variables of §8.6, as an experience STORES them.
 *
 * A variable is stored as a TOKEN and displayed with its current name, so a
 * rename invalidates nothing. An experience knows all FIVE kinds: the two a
 * protocol knows (`{{objectif:id}}`, `{{critere:id}}`, rendered from the
 * protocol the timeline plays), the two that are its own -
 * `{{champ:<blockId>}}`, the value typed into an input or ticked on a checkbox,
 * and `{{choix:<pageId>}}`, the label of the button pressed on a page that has
 * several - and `{{noyau}}`, the retained noyau of noyau.md §3.3, the one token
 * with no address: an experience has one retained noyau (N12), and its `id` in
 * `{ kind, id }` is the constant `NOYAU_VARIABLE_ID`.
 *
 * ## The token carries an ID, never a name
 *
 * A field is cited by the id of ITS BLOCK, a choice by the id of ITS PAGE. The
 * « nom de variable » of the inspector is therefore only a LABEL - not a key,
 * not an address - which is what makes renaming a variable free (§8.6). What
 * the label has to be is UNIQUE in the timeline, checked at « Enregistrer une
 * nouvelle version » and not at the keystroke, because that is the only moment
 * at which the whole timeline is in hand.
 *
 * ## Why the choice belongs to the page
 *
 * A page with several buttons remembers ONE label, in ONE named variable. A
 * lone button writes nothing: there is no choice when there is one way out, and
 * a variable that always holds the same value teaches the interviewer nothing.
 * So the token names the page, and it is `experienceProblems` - which holds the
 * whole timeline - that says whether anything on that page ever writes it.
 *
 * The French inside the token (`champ`, `choix`, `objectif`, `critere`) is
 * fixed by §8.6, which spells them so; the identifier of a KIND in code stays
 * English, like everywhere else.
 */

export type ExperienceVariable = {
  readonly kind: ExperienceVariableKind;
  readonly id: string;
};

/**
 * The four ADDRESSED token prefixes of §8.6, keyed by the English kind - and the
 * word of `{{noyau}}`, which is spelled whole by `experienceVariableToken` since
 * it carries no address.
 */
const TOKEN_PREFIX: Readonly<Record<ExperienceVariableKind, string>> = {
  field: 'champ',
  choice: 'choix',
  objective: 'objectif',
  criterion: 'critere',
  noyau: 'noyau',
};

const KIND_OF_PREFIX: Readonly<Record<string, ExperienceVariableKind>> = {
  champ: 'field',
  choix: 'choice',
  objectif: 'objective',
  critere: 'criterion',
};

/**
 * One token, anywhere in a text. Ids carry no brace and no whitespace - they
 * are struck by the editor (uuids) or by an object's store (uuids too). The
 * third group is `{{noyau}}`, whole: a `{{noyau:<id>}}` is not a token (N12).
 */
const TOKEN = /\{\{(?:(champ|choix|objectif|critere):([^{}\s]+)|(noyau))\}\}/g;

/** `{{champ:id}}` / `{{choix:id}}` / `{{objectif:id}}` / `{{critere:id}}` / `{{noyau}}`. */
export function experienceVariableToken(variable: ExperienceVariable): string {
  if (variable.kind === 'noyau') return NOYAU_TOKEN;
  return `{{${TOKEN_PREFIX[variable.kind]}:${variable.id}}}`;
}

/** The variable one match of `TOKEN` names, or null for a prefix nobody declared. */
function variableOfMatch(match: RegExpMatchArray): ExperienceVariable | null {
  const [, prefix, id, noyau] = match;
  if (noyau !== undefined) return { kind: 'noyau', id: NOYAU_VARIABLE_ID };
  if (prefix === undefined || id === undefined) return null;
  const kind = KIND_OF_PREFIX[prefix];
  return kind === undefined ? null : { kind, id };
}

/** Every variable a text carries, in order of appearance, duplicates kept. */
export function experienceVariablesOf(text: string): ExperienceVariable[] {
  const found: ExperienceVariable[] = [];
  for (const match of text.matchAll(TOKEN)) {
    const variable = variableOfMatch(match);
    if (variable !== null) found.push(variable);
  }
  return found;
}

/**
 * A text split into its literal runs and its variables, for a renderer that
 * draws a chip per token. Empty literal runs are dropped, so two adjacent
 * tokens give two segments and not three.
 */
export type ExperienceTextSegment =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'variable'; readonly variable: ExperienceVariable };

export function experienceSegmentsOf(text: string): ExperienceTextSegment[] {
  const segments: ExperienceTextSegment[] = [];
  let at = 0;
  for (const match of text.matchAll(TOKEN)) {
    const start = match.index ?? 0;
    const variable = variableOfMatch(match);
    if (variable === null) continue;
    if (start > at) segments.push({ kind: 'text', text: text.slice(at, start) });
    segments.push({ kind: 'variable', variable });
    at = start + match[0].length;
  }
  if (at < text.length) segments.push({ kind: 'text', text: text.slice(at) });
  return segments;
}

/**
 * WHERE a text is WRITTEN, for the reach rule of §8.6 (grill Variables of
 * 2026-09-16, D3): a page, by its index in the timeline; the Contexte of the
 * protocol page (§9.5); the persona overlay (§10.2). The last two are served
 * during the protocol, so for the rule they stand exactly where the protocol
 * page does.
 */
export type VariablePlace =
  | { readonly kind: 'page'; readonly pageIndex: number }
  | { readonly kind: 'context' }
  | { readonly kind: 'overlay' };

/**
 * WHERE a text lives, so a problem can point at it and the editor can open it.
 *
 * The four page fields are nullable because one site has no page and no block:
 * the persona OVERLAY of §10.2, which belongs to the experience and to no page.
 * The Contexte belongs to the experience too, but it is EDITED on the protocol
 * page, so its site names that page - « Ouvrir » has somewhere to land - and
 * no block. `place` is the same location seen by the reach rule.
 */
export type ExperienceTextSite = {
  readonly pageId: string | null;
  readonly pageIndex: number | null;
  readonly pageName: string | null;
  readonly blockId: string | null;
  readonly text: string;
  readonly place: VariablePlace;
  /**
   * The STABLE identifier of a site that is a field of a page and not a block
   * (prolongation.md §3.2, issue #186): `conversation-context`, the « Contexte
   * servi à l'interviewer » of a page played in the conversation. Absent on a
   * block's text, on the protocol page's Contexte and on the overlay. Such a
   * site carries the page and a null `blockId`, like the protocol Contexte.
   */
  readonly siteId?: ExperienceTextSiteId;
};

/** The sites that are a field of a page rather than a block (see `ExperienceTextSite.siteId`). */
export type ExperienceTextSiteId = 'conversation-context';
export const CONVERSATION_CONTEXT_SITE: ExperienceTextSiteId = 'conversation-context';

type AnyContent = ExperienceContent | ExperienceDraftContent;
type AnyPages = ExperienceContent['pages'] | NonNullable<ExperienceDraftContent['pages']>;

/**
 * The protocol page, and where it sits. Null when a content carries none -
 * which a VERSION never does (§9.1) and a draft does mid-gesture.
 *
 * ONE reading of « the protocol page » for the reference extractor, the engine,
 * the freeze of §5.2 and the reach rule below: two would let the save refuse a
 * `before-protocol` on a page the session does not freeze.
 */
export function protocolPageOf(
  content: AnyContent,
): { pageId: string; pageIndex: number; pageName: string; protocol: VersionedRef | null } | null {
  const pages: AnyPages = content.pages ?? [];
  const pageIndex = pages.findIndex((page) => page.kind === 'protocol');
  const page = pages[pageIndex];
  if (page === undefined) return null;
  return { pageId: page.id, pageIndex, pageName: page.name, protocol: page.protocol };
}

/**
 * Every text of an experience that may carry a variable (§8.6): the runs of
 * each Texte block, the label of each Checkbox - §9.2 says « variables
 * acceptées » there in as many words - the label of each Lien and the text of
 * each Texte copiable (issue #137), the Contexte of the protocol page (§9.5)
 * the persona overlay (§10.2) and - issue #186 - the context of each page played
 * in the conversation (`siteId: 'conversation-context'`, prolongation.md §3.2).
 *
 * The labels of a Bouton and of an Input are NOT here, and neither is an
 * image's caption nor a Lien's ADDRESS: §9.2 grants the variables to the text
 * blocks, to the checkbox and to the two blocks of #137, and a zone that
 * accepts a token the reader would not resolve is a trap rather than a feature.
 * A Lien's address is the one zone refused for the opposite reason - it WOULD
 * resolve, into a query string handed to a third party (`linkUrlRefusal`).
 * Which block carries which text is `blockVariableTexts`'s to say, and nobody
 * else's: the playability of a page that cites `@noyau` reads the same list.
 *
 * A DISPLAY CONDITION is not a text site: it cites its variable by structure,
 * not by token, and `experienceProblems` walks the conditions on its own.
 */
export function experienceTextSitesOf(content: AnyContent): ExperienceTextSite[] {
  const sites: ExperienceTextSite[] = [];
  const pages: AnyPages = content.pages ?? [];

  pages.forEach((page, pageIndex) => {
    const at = {
      pageId: page.id,
      pageIndex,
      pageName: page.name,
      place: { kind: 'page', pageIndex } as const,
    };
    for (const temps of page.temps) {
      for (const block of temps.blocks) {
        for (const text of blockVariableTexts(block)) {
          sites.push({ ...at, blockId: block.id, text });
        }
      }
    }
    // The context of a page played in the conversation (prolongation.md §3.2): a
    // text served by the interviewer when the page is brought into the thread,
    // so it stands where a block of this page does for the reach rule - the
    // fields and choices of the pages before, the objectives after the protocol
    // page, `@noyau` after the first choice. A page that is not ticked has none.
    const conversationContext = page.conversation?.context ?? '';
    if (conversationContext !== '') {
      sites.push({
        ...at,
        blockId: null,
        text: conversationContext,
        siteId: CONVERSATION_CONTEXT_SITE,
      });
    }
  });

  const context = content.context ?? '';
  if (context !== '') {
    const protocol = protocolPageOf(content);
    sites.push({
      pageId: protocol?.pageId ?? null,
      pageIndex: protocol?.pageIndex ?? null,
      pageName: protocol?.pageName ?? null,
      blockId: null,
      text: context,
      place: { kind: 'context' },
    });
  }

  if (content.persona != null && content.persona.overlay !== '') {
    sites.push({
      pageId: null,
      pageIndex: null,
      pageName: null,
      blockId: null,
      text: content.persona.overlay,
      place: { kind: 'overlay' },
    });
  }

  return sites;
}

/* ---------------------------------------------------------------- the reach */

/**
 * One variable the timeline WRITES, as a palette lists it and as the reach rule
 * places it: a field by the id of its block, a choice by the id of its page -
 * the id the token carries (§8.6). The name may be EMPTY: a block whose name
 * has not been typed still writes a value, and the rule below has to see it;
 * a palette skips what it cannot name.
 */
export type DeclaredVariable = {
  readonly kind: 'field' | 'choice';
  readonly id: string;
  readonly name: string;
  readonly pageIndex: number;
  readonly pageName: string;
};

/**
 * Every field and choice the timeline writes, in reading order, one choice per
 * page at most.
 *
 * Whether a page writes a choice is `pageChoiceName`'s to say, and nobody
 * else's: it is the predicate the SESSION writes by (§8.6, « un bouton seul
 * n'écrit rien » - at least two buttons, one of them remembering and named),
 * so what the palette offers, what the save admits and what a session actually
 * stores are one list. A field is a field even unnamed - the session writes it
 * under its label - so its name may be empty here, and a palette skips it.
 */
export function declaredVariablesOf(content: AnyContent): DeclaredVariable[] {
  const declared: DeclaredVariable[] = [];
  const pages: AnyPages = content.pages ?? [];
  pages.forEach((page, pageIndex) => {
    for (const temps of page.temps) {
      for (const block of temps.blocks) {
        if (!isFieldBlock(block)) continue;
        declared.push({
          kind: 'field',
          id: block.id,
          name: block.variableName,
          pageIndex,
          pageName: page.name,
        });
      }
    }
    // The choice of a page comes after its fields: it is written by the button
    // that leaves the page, so it is the last value the page produces.
    const choice = pageChoiceName(page);
    if (choice !== null) {
      declared.push({ kind: 'choice', id: page.id, name: choice, pageIndex, pageName: page.name });
    }
  });
  return declared;
}

/**
 * What a text written at `place` may cite (§8.6; grill Variables, D3).
 *
 * The rule in one sentence: a value exists from the page AFTER the one that
 * produces it. So a field or a choice is in reach on the pages after its own -
 * never on its own page, whose value does not exist while the page is shown -
 * and in the Contexte and the overlay when it is declared before the protocol
 * page, since both are served while that page plays. An objective has matter
 * during the protocol and after it: in reach in the Contexte, in the overlay
 * and on the pages after the protocol page, never before. A criterion is a
 * frozen direction and is in reach everywhere; it is not listed here.
 *
 * `outOfReach` is what a palette COUNTS rather than offers: a variable one has
 * just declared and cannot find would otherwise read as a palette that lost it.
 * Only the NAMED variables are in either list, because a palette names what it
 * offers; the breach rule below sees the unnamed ones too.
 */
export type VariableReach = {
  readonly variables: readonly DeclaredVariable[];
  readonly outOfReach: readonly DeclaredVariable[];
  /** Whether an objective resolves at this place. */
  readonly objectives: boolean;
  /**
   * Whether `@noyau` resolves at this place (noyau.md §3.3): the timeline has a
   * first choice, and this text is served on a page AFTER it. Never in the
   * Contexte nor the overlay, which are served during the protocol, and a
   * Noyau block only ever comes after the protocol page.
   */
  readonly noyau: boolean;
  /**
   * The page index this text is served on - the very line `outOfReach` is cut
   * at. Rendered because « out of reach » covers TWO cases that a sentence has
   * to tell apart: a variable typed on this page (`pageIndex === servedAt`,
   * whose value does not exist yet when the text renders) and one typed later.
   * Deriving it a second time frontend-side is how the two would drift.
   */
  readonly servedAt: number;
};

/** The index of the page a text at `place` is served on. */
function servedPageIndex(content: AnyContent, place: VariablePlace): number {
  if (place.kind === 'page') return place.pageIndex;
  const protocol = protocolPageOf(content);
  // Mid-gesture, with no protocol page yet, the two served texts stand after
  // every page: nothing declared is refused for an order nobody has fixed.
  return protocol === null ? (content.pages ?? []).length : protocol.pageIndex;
}

/**
 * Whether an objective has matter at `place`: during the protocol - the Contexte
 * and the overlay are served while it plays - and on the pages after the
 * protocol page. ONE predicate for the palette and for the refusal, so the two
 * cannot disagree on the page where matter begins.
 */
function objectivesInReach(content: AnyContent, place: VariablePlace): boolean {
  if (place.kind !== 'page') return true;
  const protocol = protocolPageOf(content);
  return protocol !== null && protocol.pageIndex < place.pageIndex;
}

/**
 * Whether `@noyau` has a value at `place`: from the page AFTER the first choice,
 * never on it and never before. ONE predicate for the palette and the refusal,
 * like `objectivesInReach`.
 */
function noyauInReach(content: AnyContent, place: VariablePlace): boolean {
  const first = firstChoiceOf(content);
  return first !== null && first.pageIndex < servedPageIndex(content, place);
}

export function variableReach(content: AnyContent, place: VariablePlace): VariableReach {
  const servedAt = servedPageIndex(content, place);
  const named = declaredVariablesOf(content).filter((variable) => variable.name !== '');
  return {
    variables: named.filter((variable) => variable.pageIndex < servedAt),
    outOfReach: named.filter((variable) => variable.pageIndex >= servedAt),
    objectives: objectivesInReach(content, place),
    noyau: noyauInReach(content, place),
    servedAt,
  };
}

/**
 * Why a variable cited at `place` is out of reach there, or null when it is in
 * reach - or when it is not the reach rule's to judge: a criterion, which
 * resolves everywhere, a field or a choice the timeline does not declare at
 * all, which is `unresolved-variable`'s refusal and not this one, and a
 * `@noyau` with no first choice anywhere, which is `noyau-first-choice-missing`'s.
 *
 * For `@noyau` the declaring page is the page of the FIRST CHOICE (noyau.md
 * §3.3): the retained noyau exists from the page that follows it.
 */
export type ScopeBreach = {
  readonly reason: VariableScopeReason;
  /** The page that declares the variable - the protocol page for `before-protocol`. */
  readonly declaredPageIndex: number | null;
  readonly declaredPageName: string | null;
};

export function variableScopeBreach(
  content: AnyContent,
  place: VariablePlace,
  variable: ExperienceVariable,
): ScopeBreach | null {
  if (variable.kind === 'criterion') return null;

  if (variable.kind === 'noyau') {
    const first = firstChoiceOf(content);
    if (first === null || noyauInReach(content, place)) return null;
    return {
      reason:
        place.kind === 'page' && first.pageIndex === place.pageIndex
          ? 'same-page'
          : 'before-declaration',
      declaredPageIndex: first.pageIndex,
      declaredPageName: first.pageName,
    };
  }

  if (variable.kind === 'objective') {
    if (objectivesInReach(content, place)) return null;
    const protocol = protocolPageOf(content);
    return {
      reason: 'before-protocol',
      declaredPageIndex: protocol?.pageIndex ?? null,
      declaredPageName: protocol?.pageName ?? null,
    };
  }

  const declared = declaredVariablesOf(content).find(
    (candidate) => candidate.kind === variable.kind && candidate.id === variable.id,
  );
  if (declared === undefined) return null;
  if (declared.pageIndex < servedPageIndex(content, place)) return null;
  return {
    reason:
      place.kind === 'page' && declared.pageIndex === place.pageIndex
        ? 'same-page'
        : 'before-declaration',
    declaredPageIndex: declared.pageIndex,
    declaredPageName: declared.pageName,
  };
}

/** One named variable of the timeline: which block holds the label, and where. */
export type NamedVariable = {
  readonly name: string;
  readonly pageId: string;
  readonly pageIndex: number;
  readonly pageName: string;
  readonly blockId: string;
};

/**
 * Every variable NAME the timeline declares, in reading order (§8.6).
 *
 * An Input, a Checkbox, a Sélection and a Note each declare one. A page declares ONE choice name at
 * most - « une seule variable par page » - and it is `pageChoiceName`'s to say
 * whether it does: two remembering buttons that carry the same name are the
 * two issues of one choice, not a duplicate, and a lone button that remembers
 * writes nothing, so its name is no variable at all. The entry points at the
 * first named remembering button, which is the block the inspector shows the
 * name on. An EMPTY name declares nothing - a block whose label has not been
 * typed yet is not a variable, and counting it would make two half-filled
 * blocks a duplicate of each other.
 */
export function namedVariablesOf(content: ExperienceContent): NamedVariable[] {
  const named: NamedVariable[] = [];
  content.pages.forEach((page, pageIndex) => {
    const where = { pageId: page.id, pageIndex, pageName: page.name };
    for (const block of pageBlocks(page)) {
      if (!isFieldBlock(block)) continue;
      if (block.variableName === '') continue;
      named.push({ ...where, name: block.variableName, blockId: block.id });
    }
    const choice = pageChoiceName(page);
    const button = pageBlocks(page).find(
      (block) => block.type === 'button' && block.rememberChoice && block.variableName !== '',
    );
    if (choice !== null && button !== undefined) {
      named.push({ ...where, name: choice, blockId: button.id });
    }
  });
  return named;
}

/**
 * Every file of the media library an experience cites (§9.1): an Image block,
 * a Son block, the background of the theme, and the background of any page that
 * overrides it.
 *
 * Deduplicated, because the answer feeds two questions that both count once -
 * « does this file still exist » and « who still cites this file ».
 */
export function citedAssetIds(content: ExperienceContent): string[] {
  const cited = new Set<string>();
  const addBackground = (theme: { backgroundImageAssetId?: string | null | undefined }): void => {
    const id = theme.backgroundImageAssetId;
    if (typeof id === 'string' && id !== '') cited.add(id);
  };

  addBackground(content.theme);
  for (const page of content.pages) {
    addBackground(page.visual);
    for (const temps of page.temps) {
      for (const block of temps.blocks) {
        if (block.type !== 'image' && block.type !== 'sound') continue;
        if (block.assetId !== null) cited.add(block.assetId);
      }
    }
  }
  return [...cited];
}
